"use client";
import { User } from "@supabase/supabase-js";
import TableHeader from "./TableHeader";
import { BBUser, FloorplanConfiguration, SubscriptionPlan } from "@/types";
import { sqmToSqft } from "@/app/utils/area";
import { roundToNearest } from "@/app/utils/format-number";
import { useEffect, useState } from "react";
import "photoswipe/dist/photoswipe.css";
import { FloorplanImage } from "../Floorplans/FloorplanImage";

function DesktopComparisonTable(args: {
  projects: any[];
  decodedSlug: string;
  user?: User;
  projectMap: Map<String, FloorplanConfiguration | undefined>;
  setProjectMap: (map: any) => void;
  isBossPlan: boolean;
}) {
  const { projects, decodedSlug, user, projectMap, setProjectMap, isBossPlan } = args;
  return (
    <table className="hidden md:table table-zebra table-pin-rows max-h-96 mt-4">
      <TableHeader projects={projects} decodedSlug={decodedSlug} />
      {user && (
        <tbody>
          <tr>
            <td className="font-semibold border-r border-gray-300">
              Select a floorplan to compare
            </td>
            {projects.map((project) => {
              const floorplans: FloorplanConfiguration[] =
                project.floorplanConfigurations;
              return (
                <td className="capitalize text-wrap opacity-60 min-w-72">
                  <select
                    className="select select-bordered select-sm w-full max-w-xs"
                    onChange={(e) => {
                      const selectedFloorplan = floorplans.find(
                        (fp) => fp.id === Number(e.target.value)
                      );
                      ``;
                      setProjectMap(
                        new Map(projectMap.set(project.uuid, selectedFloorplan))
                      );
                    }}
                  >
                    <option disabled selected>
                      Pick one
                    </option>
                    {floorplans.map((fp) => {
                      return (
                        <option key={fp.id} value={fp.id}>
                          {fp.name} {roundToNearest(sqmToSqft(fp.area))} sq.ft{" "}
                          {fp.facing}
                        </option>
                      );
                    })}
                  </select>
                </td>
              );
            })}
          </tr>
          <tr>
            <td className="font-semibold border-r border-gray-300"></td>
            {projects.map((project) => {
              return (
                <td>
                  <tr>
                    {projectMap.get(project.uuid) && (
                      isBossPlan ? (
                        <img src={projectMap.get(project.uuid)?.url} />
                      ) : (
                        <div className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                          <p className="text-sm text-gray-600">Upgrade to Boss Plan to view floorplan images</p>
                          <a href="/#pricing" className="btn btn-primary btn-sm">Upgrade Now</a>
                        </div>
                      )
                    )}
                  </tr>
                </td>
              );
            })}
          </tr>
          <tr>
            <td className="font-semibold border-r border-gray-300"></td>
            {projects.map((project) => {
              return (
                <td>
                  <tr></tr>
                </td>
              );
            })}
          </tr>
        </tbody>
      )}
    </table>
  );
}

function MobileComparisonTable(args: {
  projects: any[];
  decodedSlug: string;
  user?: User;
  projectMap: Map<String, FloorplanConfiguration | undefined>;
  setProjectMap: (map: any) => void;
  isBossPlan: boolean;
}) {
  const { projects, decodedSlug, user, projectMap, setProjectMap, isBossPlan } = args;
  return (
    <div className="display md:hidden md:table table-zebra table-pin-rows mt-4">
      <table className="table table-zebra border-2 border-gray-300 rounded-lg">
        <tbody>
          {projects.map((project) => (
            <tr>
              <td>
                <div className="flex flex-col gap-2">
                  <div>{project.projectName}</div>
                  <select
                    className="select select-bordered select-sm w-full max-w-xs"
                    onChange={(e) => {
                      const selectedFloorplan =
                        project.floorplanConfigurations.find(
                          (fp: FloorplanConfiguration) =>
                            fp.id === Number(e.target.value)
                        );
                      ``;
                      setProjectMap(
                        new Map(projectMap.set(project.uuid, selectedFloorplan))
                      );
                    }}
                  >
                    <option disabled selected>
                      Pick one
                    </option>
                    {project.floorplanConfigurations.map(
                      (fp: FloorplanConfiguration) => {
                        return (
                          <option key={fp.id} value={fp.id}>
                            {fp.name} {roundToNearest(sqmToSqft(fp.area))} sq.ft{" "}
                            {fp.facing}
                          </option>
                        );
                      }
                    )}
                  </select>
                  <div className="relative w-full">
                    {projectMap.get(project.uuid) && projectMap.get(project.uuid)?.url && (
                      isBossPlan ? (
                        <FloorplanImage url={projectMap.get(project.uuid)?.url as string}/>
                      ) : (
                        <div className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                          <p className="text-sm text-gray-600">Upgrade to Boss Plan to view floorplan images</p>
                          <a href="/#pricing" className="btn btn-primary btn-sm">Upgrade Now</a>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FloorplanComparison(args: {
  projects: any[];
  decodedSlug: string;
  user?: BBUser;
}) {
  const { projects, decodedSlug, user } = args;
  const isBossPlan = user?.subscription?.plan === SubscriptionPlan.Boss;
  const [projectMap, setProjectMap] = useState<
    Map<string, FloorplanConfiguration | undefined>
  >(new Map());
  useEffect(() => {
    projects.forEach((project) => {
      projectMap.set(project.uuid, undefined);
    });
    setProjectMap(projectMap);
  }, [projects]);

  useEffect(() => {
    console.log(projectMap);
  }, [projectMap]);
  return (
    <>
      <DesktopComparisonTable
        decodedSlug={decodedSlug}
        projectMap={projectMap}
        projects={projects}
        setProjectMap={setProjectMap}
        user={user}
        isBossPlan={isBossPlan}
      />
      <MobileComparisonTable
        decodedSlug={decodedSlug}
        projectMap={projectMap}
        projects={projects}
        setProjectMap={setProjectMap}
        user={user}
        isBossPlan={isBossPlan}
      />
    </>
  );
}
